import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { GATES } from "@/data/temple";

const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["all", "All time"]];
const GOLD = "#D9B47A";
const ROSE = "#E8838E";
const aud = (n) => `$${(n ?? 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
const LABELS = {
  visit: "Days opened", chat: "Ariel chat messages", voice: "Ariel voice plays", breath: "Ankhing breath sessions", whisper: "Daily whispers received",
  intention: "Moon intentions set", partner_breath: "Partner breath sessions", testimonial: "Testimonials written", mastery: "Mastery certificates", practice: "Practices completed", checkin: "Gate check-ins",
  reflection: "Gate reflections written", partner_session: "Partner sessions started", message: "Partner notes in session", heart: "Hearts sent", love_note: "Love notes written",
};
const PRODUCTS = [["gates", "13 Gates to Ecstasy"], ["quantum", "Quantum Intimacy"], ["bundle", "Bundle (both apps)"]];
const tip = { contentStyle: { background: "#0D0908", border: "1px solid rgba(217,180,122,0.3)", color: "#F2E8DF", fontSize: 12 }, labelStyle: { color: GOLD } };
const axis = { stroke: "#A8998C", fontSize: 11, tickLine: false, axisLine: false };
const short = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

const Kpi = ({ id, label, value, sub }) => (
  <div className="bg-[#0D0908] p-5 sm:p-6">
    <p data-testid={`an-kpi-${id}`} className="font-display text-3xl sm:text-4xl text-[#D9B47A]">{value}</p>
    <p className="mt-1 text-[13px] text-[#CFC2B6]">{label}</p>
    {sub && <p className="mt-1 text-[12px] text-[#A8998C]">{sub}</p>}
  </div>
);

const Panel = ({ title, testId, children, className = "" }) => (
  <section data-testid={testId} className={`border border-[#D9B47A]/20 p-5 sm:p-6 ${className}`}>
    <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">{title}</p>
    {children}
  </section>
);

const Trend = ({ data, bucket }) => (
  <div className="mt-6 grid lg:grid-cols-2 gap-6">
    <Panel title={`Active initiates per ${bucket}`} testId="an-chart-active">
      <div className="mt-4 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
            <CartesianGrid stroke="rgba(217,180,122,0.08)" vertical={false} />
            <XAxis dataKey="date" tickFormatter={short} {...axis} minTickGap={24} />
            <YAxis allowDecimals={false} {...axis} />
            <Tooltip {...tip} labelFormatter={short} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#CFC2B6" }} />
            <Area type="monotone" dataKey="gates_active" name="13 Gates" stroke={GOLD} fill={GOLD} fillOpacity={0.15} strokeWidth={2} />
            <Area type="monotone" dataKey="quantum_active" name="Quantum" stroke={ROSE} fill={ROSE} fillOpacity={0.15} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>
    <Panel title={`Revenue and sign-ups per ${bucket}`} testId="an-chart-revenue">
      <div className="mt-4 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
            <CartesianGrid stroke="rgba(217,180,122,0.08)" vertical={false} />
            <XAxis dataKey="date" tickFormatter={short} {...axis} minTickGap={24} />
            <YAxis yAxisId="r" {...axis} />
            <YAxis yAxisId="s" orientation="right" allowDecimals={false} {...axis} />
            <Tooltip {...tip} labelFormatter={short} formatter={(v, n) => (n === "Revenue (AUD)" ? aud(v) : v)} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#CFC2B6" }} />
            <Bar yAxisId="r" dataKey="revenue" name="Revenue (AUD)" fill="#C2414F" radius={[2, 2, 0, 0]} />
            <Bar yAxisId="s" dataKey="signups" name="Sign-ups" fill={GOLD} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  </div>
);

const AppPanel = ({ id, name, color, app }) => (
  <Panel title={name} testId={`an-app-${id}`}>
    <div className="mt-4 grid grid-cols-3 gap-3">
      {[["members", "Members"], ["new_members", "New in range"], ["active", "Active"]].map(([k, l]) => (
        <div key={k}><p data-testid={`an-${id}-${k}`} className="font-display text-3xl" style={{ color }}>{app?.[k] ?? 0}</p><p className="text-[12px] text-[#A8998C]">{l}</p></div>
      ))}
    </div>
    <ul className="mt-5 divide-y divide-[#D9B47A]/10">
      {Object.entries(app?.activity ?? {}).map(([k, v]) => (
        <li key={k} data-testid={`an-${id}-activity-${k}`} className="flex items-center justify-between py-2 text-[14px]"><span className="text-[#CFC2B6]">{LABELS[k] ?? k}</span><span className="font-tt-mono text-[#F2E8DF]">{v}</span></li>
      ))}
    </ul>
  </Panel>
);

const Progress = ({ title, testId, values, color }) => {
  const data = GATES.map((g, i) => ({ name: `${i + 1}`, full: g.name, v: values?.[i] ?? 0 }));
  return (
    <Panel title={title} testId={testId}>
      <div className="mt-4 h-52">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -24, right: 4, top: 8 }}>
            <CartesianGrid stroke="rgba(217,180,122,0.08)" vertical={false} />
            <XAxis dataKey="name" {...axis} interval={0} />
            <YAxis allowDecimals={false} {...axis} />
            <Tooltip {...tip} labelFormatter={(l, p) => `Gate ${l} · ${p?.[0]?.payload?.full ?? ""}`} formatter={(v) => [v, "Initiates"]} />
            <Bar dataKey="v" fill={color} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
};

// Admin: usage and sales across 13 Gates to Ecstasy and Quantum Intimacy.
export default function AnalyticsAdmin({ http, withRefresh }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    withRefresh(() => http.get("/admin/analytics", { params: { range } }))
      .then(({ data: d }) => setData(d ?? null))
      .catch(() => toast.error("Could not load analytics."))
      .finally(() => setLoading(false));
  }, [http, withRefresh, range]);
  useEffect(() => {
    load();
  }, [load]);
  const t = data?.totals ?? {};
  const sales = data?.sales ?? {};
  const w = data?.whispers ?? {};
  const trend = Array.isArray(data?.trend) ? data.trend : [];
  const top = Array.isArray(data?.top_practices) ? data.top_practices : [];
  return (
    <div data-testid="analytics-admin" className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="radiogroup" aria-label="Time range" className="flex gap-2 overflow-x-auto">
          {RANGES.map(([k, l]) => (
            <button key={k} role="radio" aria-checked={range === k} data-testid={`an-range-${k}`} onClick={() => setRange(k)} className={`shrink-0 rounded-full border px-4 py-2 text-[13px] transition-colors ${range === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/25 text-[#CFC2B6] hover:border-[#D9B47A]"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="an-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-[13px] text-[#A8998C] hover:text-[#F2E8DF]">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh</button>
      </div>
      {!data ? <Loader2 className="mt-10 w-5 h-5 animate-spin text-[#D9B47A]" /> : (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#D9B47A]/20 border border-[#D9B47A]/20">
            <Kpi id="revenue" label="Revenue (AUD)" value={aud(t.revenue)} sub={`${t.orders ?? 0} orders`} />
            <Kpi id="signups" label="Sign-ups" value={t.signups ?? 0} sub={`${t.people ?? 0} accounts in total`} />
            <Kpi id="active" label="Active initiates" value={t.active ?? 0} sub="Used either app" />
            <Kpi id="both" label="Own both apps" value={t.both_apps ?? 0} sub="All time" />
            <Kpi id="conversion" label="Sign-up to purchase" value={t.signups ? `${Math.round(((t.orders ?? 0) / t.signups) * 100)}%` : "—"} sub="Orders ÷ sign-ups in range" />
          </div>
          <Trend data={trend} bucket={data.bucket === "week" ? "week" : "day"} />
          <Panel title="Sales by product" testId="an-sales" className="mt-6">
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead><tr className="text-left text-[12px] text-[#A8998C]"><th className="py-2 font-normal">Product</th><th className="py-2 font-normal text-right">Orders</th><th className="py-2 font-normal text-right">Partner price</th><th className="py-2 font-normal text-right">Revenue</th><th className="py-2 font-normal text-right">Refunds</th></tr></thead>
                <tbody className="divide-y divide-[#D9B47A]/10">
                  {PRODUCTS.map(([k, l]) => (
                    <tr key={k} data-testid={`an-sales-${k}`}><td className="py-2.5 text-[#F2E8DF]">{l}</td><td className="py-2.5 text-right font-tt-mono">{sales[k]?.count ?? 0}</td><td className="py-2.5 text-right font-tt-mono text-[#E8838E]">{sales[k]?.partner ?? 0}</td><td className="py-2.5 text-right font-tt-mono text-[#D9B47A]">{aud(sales[k]?.revenue)}</td><td className="py-2.5 text-right font-tt-mono text-[#A8998C]">{sales[k]?.refunds ?? 0}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <AppPanel id="gates" name="13 Gates to Ecstasy" color={GOLD} app={data.apps?.gates} />
            <AppPanel id="quantum" name="Quantum Intimacy" color={ROSE} app={data.apps?.quantum} />
          </div>
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <Progress title="13 Gates · initiates who opened each gate (all time)" testId="an-progress-gates" values={data.progress?.gates_opened} color={GOLD} />
            <Progress title="Quantum · initiates who sealed each gate (all time)" testId="an-progress-quantum" values={data.progress?.quantum_sealed} color={ROSE} />
          </div>
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <Panel title="Most practised in Quantum Intimacy" testId="an-top-practices">
              {top.length ? (
                <ol className="mt-4 space-y-2">{top.map((p, k) => <li key={p.title} data-testid={`an-top-${k}`} className="flex justify-between gap-3 text-[14px]"><span className="text-[#CFC2B6] truncate">{k + 1}. {p.title}</span><span className="font-tt-mono text-[#F2E8DF]">{p.count}</span></li>)}</ol>
              ) : <p className="mt-4 text-[14px] text-[#A8998C]">No practices logged in this range yet.</p>}
            </Panel>
            <Panel title="Daily whispers" testId="an-whispers">
              <p className="mt-4 font-display text-3xl text-[#D9B47A]">{w.sent ? `${Math.round(((w.kept ?? 0) / w.sent) * 100)}%` : "—"}</p>
              <p className="text-[13px] text-[#CFC2B6]">kept by initiates · {w.kept ?? 0} of {w.sent ?? 0} whispers</p>
            </Panel>
          </div>
          <p className="mt-6 text-[12px] text-[#A8998C]">Your own admin account is left out of every figure. Days opened and voice plays are counted from today onwards.</p>
        </>
      )}
    </div>
  );
}
